import {
  Box,
  Container,
  createTheme,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import Title from "../../components/Pages/Title";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import StatusCard from "../../components/Pages/StatusCard";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useEffect, useState } from "react";
import { Transaction } from "../../openApi/api";
import * as Api from "../../openApi";
import { CalculatePassTime } from "../../utils";
import Pagination from "../../components/Pagination";
import { useRecoilState } from "recoil";
import { ThemeLight } from "../../state/ThemeLight";
import { useUpcxChain } from "../../hook/useUpcxChain";
import { TransactionsService } from "../../backend";

const AllTsx = () => {
  const { selectedShard } = useUpcxChain();

  const [themeLight] = useRecoilState(ThemeLight);

  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const theme = createTheme({
    palette: {
      mode: themeLight ? "light" : "dark",
    },
  });

  const handleChange = (event: SelectChangeEvent) => {
    setLimit(parseInt(event.target.value));
    setPage(0);
  };

  const getTransactions = async () => {
    const result = await TransactionsService.getTransactions(
      undefined,
      limit,
      page
    );
    setTransactions(result.txs ?? []);
    setTotal(result.total ?? 0);
  };

  useEffect(() => {
    if (!selectedShard) {
      return;
    }

    getTransactions();
    //eslint-disable-next-line
  }, [limit, page, selectedShard]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <Title title="Transactions" smallTitle="" />

        <Container className="container-xxl pt-5 pb-12">
          <Grid container spacing={2} className="g-4 mb-4">
            <StatusCard
              title="Transactions"
              value={total.toLocaleString()}
              sufix=""
              sufixCls="text-danger"
            />
            <StatusCard
              title="Pending Transactions (Last 1H)"
              value="0"
              sufix="(Average)"
              sufixCls=""
            />
            <StatusCard
              title="Network Transactions Fee (24H)"
              value="0.00000 UPC"
              sufix="(0.00%)"
              sufixCls="text-success"
            />
            <StatusCard
              title="Avg. Transaction Fee (24h)"
              value="0.00 USD"
              sufix="(0.00%)"
              sufixCls="text-danger"
            />
          </Grid>

          <Box className="card">
            <Box className="card-body">
              <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <Box>
                  <span className="text-dark gap-1">
                    {total.toLocaleString()} transactions found
                  </span>
                  <Box className="d-block text-muted small">
                    (Showing the last 500k records)
                  </Box>
                </Box>
                <Box className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <Pagination
                    limit={limit}
                    page={page}
                    setPage={setPage}
                    total={total}
                  />
                </Box>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              className="box-shadow-0 table-striped"
            >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell width={10} align="center">
                      <Tooltip
                        title={"See preview of the transaction details."}
                        placement="right"
                      >
                        <HelpOutlineIcon className="text-icon text-muted" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left" width={1}>
                      Txn Hash
                    </TableCell>
                    <TableCell align="left">
                      <Box className="d-flex align-items-center">
                        Method{" "}
                        <Tooltip
                          title={
                            "Function executed based on decoded input data. For unidentified functions, method ID is displayed instead."
                          }
                          placement="right"
                        >
                          <HelpOutlineIcon className="text-icon text-muted" />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="left">Block</TableCell>
                    <TableCell align="left">
                      <Link>Age</Link>
                    </TableCell>
                    <TableCell align="left">From</TableCell>
                    <TableCell align="left"></TableCell>
                    <TableCell align="left">To</TableCell>
                    <TableCell align="left">Value</TableCell>
                    <TableCell align="left">
                      <Link>Txn Fee</Link>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {transactions.map((data) => {
                    let value = "";
                    let toValue = "";
                    if (
                      data.actionName === "transfer" ||
                      data.actionName === "issue" ||
                      data.actionName === "swap" ||
                      data.actionName === "multitransfer"
                    ) {
                      value = JSON.parse(data.data).quantity;
                    }
                    if (
                      data.actionName === "swap" ||
                      data.actionName === "multitransfer"
                    ) {
                      toValue = data.swapAmount
                        ? `${data.swapAmount} ${data.swapToken}`
                        : "";
                    }

                    if (!value) {
                      const txData = JSON.parse(data.data ?? {});
                      value = txData.quantity ?? txData.price ?? "";
                      toValue = "";
                    }
                    return (
                      <TableRow>
                        <TableCell align="center">
                          <Box
                            className="js-tnx-preview btn btn-sm btn-white fs-70x content-center mx-auto myFnExpandBox"
                            sx={{
                              width: "1.75rem",
                              height: "1.75rem",
                            }}
                          >
                            <RemoveRedEyeOutlinedIcon className="small-icon" />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="hash-tag text-truncate">
                            <Link
                              href={`/tx/${data.txId}`}
                              className="myFnExpandBox_searchVal"
                            >
                              {data.txId}
                            </Link>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="d-block badge bg-light border border-dark dark:border-white border-opacity-10 text-dark fw-normal text-truncate w-100 py-1.5">
                            <Tooltip
                              describeChild
                              title="Transfer"
                              placement="top"
                            >
                              <span>{data.actionName}</span>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Link href={`/block/${data.blockNum}`}>
                            {data.blockNum}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={`${CalculatePassTime(
                              new Date(data.timestamp.substring(0, 19))
                            )} ago`}
                            placement="top"
                          >
                            <Box rel="tooltip">
                              {CalculatePassTime(
                                new Date(data.timestamp.substring(0, 19))
                              )}{" "}
                              ago
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box className="d-flex d-flex align-items-center gap-1">
                            {data.sender.includes("upcx.") && (
                              <Tooltip title="Contract" placement="top">
                                <AssignmentIcon className="text-icon text-secondary" />
                              </Tooltip>
                            )}
                            <Link
                              href={`/address/${data.sender}`}
                              className="hash-tag text-truncate border px-1 border-0"
                            >
                              {data.sender}
                            </Link>

                            <Tooltip title="Copy Address" placement="top">
                              <Link className="js-clipboard link-secondary ">
                                <ContentCopyIcon
                                  className="text-icon"
                                  onClick={() =>
                                    navigator.clipboard.writeText(data.sender)
                                  }
                                />
                              </Link>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            className="bg-success bg-opacity-10 border border-success border-opacity-25 text-success fs-70x content-center rounded-pill mx-auto"
                            sx={{
                              width: "1.5rem",
                              height: "1.5rem",
                            }}
                          >
                            <ArrowRightAltIcon className="text-icon" />
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box className="d-flex d-flex align-items-center gap-1">
                            <Box className="d-flex align-items-center gap-1 border px-1 border-0">
                              {data.receiver.includes("upcx.") && (
                                <Tooltip title="Contract" placement="top">
                                  <AssignmentIcon className="text-icon text-secondary" />
                                </Tooltip>
                              )}

                              <Tooltip
                                title="Public Tag: Fee Recipient: 0xe68...127 (0xe688b84b23f322a994a53dbf8e15fa82cdb71127)"
                                placement="top"
                              >
                                <Link
                                  href={`/address/${data.receiver}`}
                                  className=""
                                  data-bs-toggle="tooltip"
                                  data-bs-trigger="hover"
                                  data-bs-html="true"
                                  data-bs-title="0x1dde41f92d3f7a46ce6f6de24ab3a94a45a3d708"
                                >
                                  {data.receiver}
                                </Link>
                              </Tooltip>
                            </Box>

                            {data.receiver !== "" && (
                              <Tooltip title="Copy Address" placement="top">
                                <Link className="js-clipboard link-secondary ">
                                  <ContentCopyIcon
                                    className="text-icon"
                                    onClick={() =>
                                      navigator.clipboard.writeText(
                                        data.receiver
                                      )
                                    }
                                  />
                                </Link>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={value} placement="top">
                            <Box>
                              {value}
                              {toValue && (
                                <>
                                  &nbsp;
                                  <ArrowRightAltIcon className="text-icon" />
                                  &nbsp;
                                  {toValue}
                                </>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-muted">{`${data.fee.toFixed(
                          5
                        )} UPC`}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box className="card-body">
              <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <Box
                  id="ContentPlaceHolder1_pageRecords"
                  className="d-flex align-items-center gap-2 text-nowrap text-muted"
                >
                  Show:
                  <FormControl sx={{ m: 1, minWidth: 70 }}>
                    <Select
                      value={limit.toString()}
                      onChange={handleChange}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem
                        value={10}
                        sx={{
                          fontSize: "12px;",
                        }}
                      >
                        10
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={25}
                      >
                        25
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={50}
                      >
                        50
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: "12px;",
                        }}
                        value={100}
                      >
                        100
                      </MenuItem>
                    </Select>
                  </FormControl>
                  Records
                </Box>
                <Box className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <Pagination
                    limit={limit}
                    page={page}
                    setPage={setPage}
                    total={total}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    </>
  );
};

export default AllTsx;
